<template>
  <page-header-wrapper @back="back">
    <template v-slot:title>
      {{ formTitle }}
    </template>
    <a-card :bordered="false">
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 2}" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="状态" prop="status">
          <a-select v-model="form.status" placeholder="请选择" style="width: 10%">
            <a-select-option :value="0">禁用</a-select-option>
            <a-select-option :value="1">正常</a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="语言" required class="locale-select-item">
          <a-select
            mode="multiple"
            v-model="selectedLocales"
            placeholder="请选择语言"
            class="locale-select"
            :maxTagCount="4"
            :allowClear="false"
            @change="handleLocaleChange"
          >
            <a-select-option v-for="item in localeFields" :key="item.key" :value="item.key">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <div v-for="item in visibleLocaleFields" :key="item.key" class="locale-block">
          <a-form-model-item :label="item.label + '标题'" :prop="item.title">
            <a-input size="large" v-model="form[item.title]" :placeholder="'请输入' + item.label + '标题'" />
          </a-form-model-item>
          <a-form-model-item :label="item.label + '内容'" :prop="item.content">
            <editor
              :editorValue="item.content"
              :configValue="item.content"
              v-model="form[item.content]"
            />
          </a-form-model-item>
        </div>
      </a-form-model>
      <a-space style="margin-left: 8.5%">
        <a-button type="primary" :loading="submitLoading" :disabled="submitLoading" @click="submitForm">
          发布
        </a-button>
        <a-button type="dashed" @click="back">
          取消
        </a-button>
      </a-space>
    </a-card>
  </page-header-wrapper>
</template>

<script>

import { getNotice, addNotice, updateNotice } from '@/api/system/notice'
import Editor from '@/components/Editor'
import bus from '@/utils/bus'
import events from '@/components/MultiTab/events'

const LOCALE_FIELDS = [
  { key: 'zh', label: '中文简体', title: 'noticeTitle', content: 'noticeContent' },
  { key: 'tc', label: '中文繁体', title: 'noticeTitleTc', content: 'noticeContentTc' },
  { key: 'en', label: '英文', title: 'noticeTitleEn', content: 'noticeContentEn' },
  { key: 'jp', label: '日语', title: 'noticeTitleJp', content: 'noticeContentJp' },
  { key: 'ko', label: '韩语', title: 'noticeTitleKo', content: 'noticeContentKo' },
  { key: 'fr', label: '法语', title: 'noticeTitleFr', content: 'noticeContentFr' },
  { key: 'ab', label: '阿拉伯语', title: 'noticeTitleAb', content: 'noticeContentAb' },
  { key: 'vi', label: '越南语', title: 'noticeTitleVi', content: 'noticeContentVi' }
]

const DEFAULT_SELECTED_LOCALES = ['zh']

function buildLocaleFormFields () {
  const fields = {}
  LOCALE_FIELDS.forEach(item => {
    fields[item.title] = null
    fields[item.content] = ''
  })
  return fields
}

function hasLocaleContent (form, item) {
  const title = form[item.title]
  const content = form[item.content]
  return !!(title && String(title).trim()) || !!(content && String(content).replace(/<[^>]+>/g, '').trim())
}

function getBaseFormFields () {
  return {
    id: null,
    noticeType: null,
    status: 1,
    bstatus: 1,
    ...buildLocaleFormFields()
  }
}

export default {
  name: 'NoticeForm',
  components: {
    Editor
  },
  dicts: ['sys_notice_status', 'sys_notice_type'],
  data () {
    return {
      wrapperCol: { span: 48 },
      submitLoading: false,
      total: 0,
      id: undefined,
      formTitle: '',
      localeFields: LOCALE_FIELDS,
      selectedLocales: [...DEFAULT_SELECTED_LOCALES],
      form: getBaseFormFields(),
      rules: {
        status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
      },
      open: false
    }
  },
  computed: {
    visibleLocaleFields () {
      return this.localeFields.filter(item => this.selectedLocales.includes(item.key))
    }
  },
  mounted () {
    this.id = this.$route.params.id
    this.formTitle = this.$route.params.formTitle
    if (this.id) {
      this.handleUpdate(this.id)
    } else {
      this.handleAdd()
    }
  },
  methods: {
    handleLocaleChange (value) {
      if (!value || value.length === 0) {
        this.selectedLocales = [...DEFAULT_SELECTED_LOCALES]
        this.$message.warning('请至少选择一种语言')
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    resolveSelectedLocalesFromData (data) {
      const keys = LOCALE_FIELDS
        .filter(item => hasLocaleContent(data || {}, item))
        .map(item => item.key)
      return keys.length ? keys : [...DEFAULT_SELECTED_LOCALES]
    },
    validateLocaleContent () {
      if (!this.selectedLocales || this.selectedLocales.length === 0) {
        this.$message.error('请至少选择一种语言')
        return false
      }
      const hasOneLocaleFilled = this.visibleLocaleFields.some(item => hasLocaleContent(this.form, item))
      if (!hasOneLocaleFilled) {
        this.$message.error('至少选择一种语言填写标题或内容，不能全部为空')
        return false
      }
      return true
    },
    reset () {
      this.form = getBaseFormFields()
      this.selectedLocales = [...DEFAULT_SELECTED_LOCALES]
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    handleAdd () {
      this.reset()
      this.formTitle = '添加公告'
    },
    handleUpdate (id) {
      this.reset()
      getNotice(id).then(response => {
        const data = response.data || {}
        const form = { ...getBaseFormFields(), ...data }
        LOCALE_FIELDS.forEach(item => {
          form[item.title] = data[item.title] != null ? data[item.title] : null
          form[item.content] = data[item.content] != null ? data[item.content] : ''
        })
        form.bstatus = data.status
        this.form = form
        this.selectedLocales = this.resolveSelectedLocalesFromData(form)
        this.formTitle = '修改公告'
      })
    },
    buildSubmitPayload () {
      const payload = {
        id: this.form.id != null ? this.form.id : null,
        noticeType: this.form.noticeType != null ? this.form.noticeType : null,
        status: this.form.status != null ? this.form.status : 1
      }
      LOCALE_FIELDS.forEach(item => {
        payload[item.title] = this.form[item.title] != null ? this.form[item.title] : ''
        payload[item.content] = this.form[item.content] != null ? this.form[item.content] : ''
      })
      return payload
    },
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }
        if (!this.validateLocaleContent()) {
          return false
        }
        this.submitLoading = true
        this.$nextTick(() => {
          const formData = this.buildSubmitPayload()
          const request = formData.id != null ? updateNotice(formData) : addNotice(formData)
          request.then(() => {
            this.$message.success(formData.id != null ? '修改成功' : '新增成功', 3)
            bus.$emit('updateNoticeList')
            this.back()
          }).finally(() => {
            this.submitLoading = false
          })
        })
      })
    },
    back () {
      const pages = this.$store.pages
      events.$emit('close', pages.indexOf(this.$route))
      this.$router.push('/system/notice')
    }
  }
}
</script>

<style scoped lang="less">
.locale-select-item {
  margin-bottom: 16px;
}
.locale-select {
  width: 60%;
  min-width: 320px;
}
.locale-block {
  padding: 12px 0 4px;
  border-top: 1px dashed #f0f0f0;
  margin-bottom: 8px;
}
</style>
